import DesignButton from 'components/button/DesignButton';
import { TableModel } from 'components/table/IdmTable';
import { CapacityPlanDeliveryRow } from 'core/graphql/CapacityPlanning/models';
import { CapacityPlanGeocodeStatus } from 'core/graphql/types';
import { DeliveryAddressDraft } from './DeliveryAddress';
import GeocodeStatusBadge from './GeocodeStatusBadge';

/**
 * Columns of the Deliveries table, including the inline coordinate editor the
 * Agency Supervisor uses when the Address Processing Flow could not place a pin
 * (or placed it badly), and the address editor for invalid or failed-geocode
 * rows.
 */

/** Which inline editor is open; only one row and one editor at a time. */
export type DeliveryEditor = 'coordinates' | 'address';

export interface DeliveriesTableActions {
  /** False while the Run is Queued or Computing, or the user may not edit. */
  canEditRows: boolean;
  editing: { id: number; editor: DeliveryEditor } | null;
  latitudeDraft: string;
  longitudeDraft: string;
  addressDraft: DeliveryAddressDraft;
  isSaving: boolean;
  onEdit: (row: CapacityPlanDeliveryRow) => void;
  onEditAddress: (row: CapacityPlanDeliveryRow) => void;
  onCancel: () => void;
  onSave: () => void;
  onLatitudeChange: (value: string) => void;
  onLongitudeChange: (value: string) => void;
  onAddressChange: (field: keyof DeliveryAddressDraft, value: string) => void;
}

const dash = <span className="text-light-black dark:text-sublte-white">—</span>;

const text = (value: string | null | undefined) =>
  value ? <span>{value}</span> : dash;

const subClassName = 'text-xs text-light-black dark:text-sublte-white';

const inputClassName =
  'h-7 w-full rounded-sm px-2 text-xs outline outline-1 hover:outline-design-red dark:bg-design-black dark:[color-scheme:dark]';

const isEditable = (row: CapacityPlanDeliveryRow) =>
  !row.validationError &&
  row.geocodeStatus !== CapacityPlanGeocodeStatus.Pending;

const isAddressEditable = (row: CapacityPlanDeliveryRow) =>
  !!row.validationError ||
  row.geocodeStatus === CapacityPlanGeocodeStatus.Failed;

const isEditing = (
  row: CapacityPlanDeliveryRow,
  actions: DeliveriesTableActions,
  editor: DeliveryEditor,
) => actions.editing?.id === row.id && actions.editing?.editor === editor;

const addressFields: [keyof DeliveryAddressDraft, string][] = [
  ['address', 'Address'],
  ['town', 'Town'],
  ['village', 'Village'],
  ['county', 'County'],
];

const addressEditor = (
  row: CapacityPlanDeliveryRow,
  actions: DeliveriesTableActions,
) => (
  <div className="flex flex-col gap-1">
    {addressFields.map(([field, label]) => (
      <input
        key={field}
        aria-label={`${label} for row ${row.rowNumber}`}
        type="text"
        placeholder={label}
        className={inputClassName}
        value={actions.addressDraft[field]}
        onChange={(event) => actions.onAddressChange(field, event.target.value)}
      />
    ))}
  </div>
);

const addressCell = (
  row: CapacityPlanDeliveryRow,
  actions: DeliveriesTableActions,
) => {
  if (isEditing(row, actions, 'address')) {
    return addressEditor(row, actions);
  }

  const locality = [row.village, row.county].filter(Boolean).join(', ');
  return (
    <div className="flex flex-col">
      {text(row.address)}
      {locality && <span className={subClassName}>{locality}</span>}
    </div>
  );
};

const coordinateEditor = (
  row: CapacityPlanDeliveryRow,
  actions: DeliveriesTableActions,
) => (
  <div className="flex flex-col gap-1">
    <input
      aria-label={`Latitude for row ${row.rowNumber}`}
      type="text"
      placeholder="Latitude"
      className={inputClassName}
      value={actions.latitudeDraft}
      onChange={(event) => actions.onLatitudeChange(event.target.value)}
    />
    <input
      aria-label={`Longitude for row ${row.rowNumber}`}
      type="text"
      placeholder="Longitude"
      className={inputClassName}
      value={actions.longitudeDraft}
      onChange={(event) => actions.onLongitudeChange(event.target.value)}
    />
  </div>
);

const coordinatesCell = (
  row: CapacityPlanDeliveryRow,
  actions: DeliveriesTableActions,
) => {
  if (isEditing(row, actions, 'coordinates')) {
    return coordinateEditor(row, actions);
  }

  const { latitude, longitude } = row;
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    if (!row.validationError) {
      return <span className={`${subClassName} text-darker-red`}>missing</span>;
    }
    return dash;
  }

  return (
    <div className="flex flex-col">
      <span>{latitude.toFixed(6)}</span>
      <span>{longitude.toFixed(6)}</span>
      {row.isApproximated && <span className={subClassName}>approximated</span>}
      {row.outOfCountry && (
        <span className={`${subClassName} text-darker-red`}>
          outside the country
        </span>
      )}
    </div>
  );
};

/**
 * An invalid row never reached the geocoder, so it carries no badge - the
 * validation error takes the place of the status.
 */
const geocodingCell = (row: CapacityPlanDeliveryRow) => {
  if (row.validationError) {
    return (
      <span className="block max-w-full truncate text-xs text-darker-red">
        {row.validationError}
      </span>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <GeocodeStatusBadge status={row.geocodeStatus} />
      {row.geocodeStatus === CapacityPlanGeocodeStatus.Failed &&
        row.geocodeError && (
          <span
            title={row.geocodeError}
            className={`block max-w-full truncate ${subClassName}`}
          >
            {row.geocodeError}
          </span>
        )}
    </div>
  );
};

const actionsCell = (
  row: CapacityPlanDeliveryRow,
  actions: DeliveriesTableActions,
) => {
  if (actions.editing?.id === row.id) {
    return (
      <div className="flex justify-end gap-1">
        <DesignButton
          variant="primary"
          size="sm"
          disabled={actions.isSaving}
          onClickHandler={actions.onSave}
        >
          Save
        </DesignButton>
        <DesignButton
          variant="ghost"
          size="sm"
          disabled={actions.isSaving}
          onClickHandler={actions.onCancel}
        >
          Cancel
        </DesignButton>
      </div>
    );
  }

  // Spec 16.5: a Pending row may still be answered by the Address Processing
  // Flow, and that answer would overwrite the pin, so it has no editor.
  if (row.geocodeStatus === CapacityPlanGeocodeStatus.Pending) {
    return <span className={subClassName}>Waiting…</span>;
  }

  if (!isEditable(row) && !isAddressEditable(row)) {
    return dash;
  }

  return (
    <div className="flex flex-col items-end gap-1">
      {isEditable(row) && (
        <DesignButton
          variant="secondary"
          size="sm"
          ariaLabel={`Set coordinates for row ${row.rowNumber}`}
          disabled={!actions.canEditRows}
          onClickHandler={() => actions.onEdit(row)}
        >
          Set coordinates
        </DesignButton>
      )}
      {isAddressEditable(row) && (
        <DesignButton
          variant="secondary"
          size="sm"
          ariaLabel={`Edit address for row ${row.rowNumber}`}
          disabled={!actions.canEditRows}
          onClickHandler={() => actions.onEditAddress(row)}
        >
          Edit address
        </DesignButton>
      )}
    </div>
  );
};

export const createDeliveriesTableModel = (
  actions: DeliveriesTableActions,
): TableModel<CapacityPlanDeliveryRow> => ({
  columns: [
    {
      title: 'Row',
      html: (row) => <span>{row.rowNumber}</span>,
      width: 8,
      align: 'right',
    },
    {
      title: 'Address',
      html: (row) => addressCell(row, actions),
      width: 24,
      align: 'left',
    },
    { title: 'Town', html: (row) => text(row.town), width: 11, align: 'left' },
    {
      title: 'Delivery ID',
      html: (row) => text(row.deliveryId),
      width: 9,
      align: 'left',
    },
    {
      title: 'Coordinates',
      html: (row) => coordinatesCell(row, actions),
      width: 17,
      align: 'left',
    },
    {
      title: 'Geocoding',
      html: (row) => geocodingCell(row),
      width: 21,
      align: 'left',
    },
    {
      title: 'Actions',
      html: (row) => actionsCell(row, actions),
      width: 10,
      align: 'right',
    },
  ],
});
